import { useEffect, useRef } from 'react';

import { downloadBoth, downloadSheet, type Sheets } from '../../lib/export';
import styles from './ExportView.module.css';

interface Props {
  result: Sheets | null;
  /** Which sheet the mobile view is showing. */
  tab: keyof Sheets;
  isDesktop: boolean;
  onTab: (tab: keyof Sheets) => void;
  onBack: () => void;
}

const CAPTIONS: Record<keyof Sheets, string> = {
  quiz: '81 squares blacked out. Send this one to the guessers.',
  solution: 'Gridlines only, picture showing through. Keep this to yourself.',
};

/**
 * The generated sheets.
 *
 * Stays mounted and slides in, so the transform/opacity transition the handoff specifies
 * actually runs; `inert` keeps it out of the tab order and the accessibility tree while
 * hidden.
 */
export function ExportView({
  result,
  tab,
  isDesktop,
  onTab,
  onBack,
}: Props): React.JSX.Element {
  const open = result !== null;
  const cancelRef = useRef<(() => void) | null>(null);

  // Drop a pending second download if the view closes before its timer fires.
  useEffect(() => {
    if (!open && cancelRef.current !== null) {
      cancelRef.current();
      cancelRef.current = null;
    }
  }, [open]);

  useEffect(() => () => cancelRef.current?.(), []);

  const both = (): void => {
    if (result === null) return;
    cancelRef.current?.();
    cancelRef.current = downloadBoth(result);
  };

  return (
    <div className={styles.panel} data-open={open} inert={!open}>
      <div className={styles.header}>
        <h2 className={styles.title}>Two sheets, ready to play</h2>
        {isDesktop ? (
          <>
            <button className="btn btn-secondary" type="button" onClick={onBack}>
              Back to the board
            </button>
            <button className="btn btn-primary" type="button" onClick={both}>
              Download both
            </button>
          </>
        ) : (
          <button className="btn btn-ghost" type="button" onClick={onBack}>
            Back
          </button>
        )}
      </div>

      {result !== null && isDesktop && (
        <div className={styles.columns}>
          <section className={styles.column}>
            <div className={styles.tagRow}>
              <span className="tag tag-accent">For players</span>
              <span className={styles.caption}>81 squares blacked out</span>
            </div>
            <img className={styles.preview} src={result.quiz} alt="Player sheet preview" />
            <button
              className="btn btn-secondary btn-block"
              type="button"
              onClick={() => downloadSheet(result, 'quiz')}
            >
              Download player sheet
            </button>
          </section>

          <section className={styles.column}>
            <div className={styles.tagRow}>
              <span className="tag tag-neutral">Answer key</span>
              <span className={styles.caption}>gridlines only — keep this to yourself</span>
            </div>
            <img className={styles.preview} src={result.solution} alt="Answer key preview" />
            <button
              className="btn btn-secondary btn-block"
              type="button"
              onClick={() => downloadSheet(result, 'solution')}
            >
              Download answer key
            </button>
          </section>
        </div>
      )}

      {result !== null && !isDesktop && (
        <div className={styles.mobileBody}>
          <div className={`seg ${styles.seg}`}>
            <label className="seg-opt">
              <input
                type="radio"
                name="sheet"
                checked={tab === 'quiz'}
                onChange={() => onTab('quiz')}
              />
              For players
            </label>
            <label className="seg-opt">
              <input
                type="radio"
                name="sheet"
                checked={tab === 'solution'}
                onChange={() => onTab('solution')}
              />
              Answer key
            </label>
          </div>

          <img
            className={styles.preview}
            src={result[tab]}
            alt={tab === 'quiz' ? 'Player sheet preview' : 'Answer key preview'}
          />
          <p className={styles.caption}>{CAPTIONS[tab]}</p>

          <div className={styles.mobileActions}>
            <button className="btn btn-primary btn-block" type="button" onClick={both}>
              Download both
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => downloadSheet(result, 'quiz')}
            >
              Just the player sheet
            </button>
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => downloadSheet(result, 'solution')}
            >
              Just the answer key
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
